import { createContext, FC, useContext, useEffect, useState, ReactNode } from "react";
import { useAuthContext } from "../hooks/Auth";
import { environment } from "../config";
import { getInfoCredits } from "../api/broker/dashboard";
import { toast } from "react-toastify";

const { REACT_APP_API_CONTROL_TRANSACCIONAL, VERSION } = environment;
const baseApiBroker = REACT_APP_API_CONTROL_TRANSACCIONAL;

export interface UserOrganization {
    id_organizacion: string;
    nombre_organizacion: string;
    disabled?: boolean;
}

export interface UserRole {
    id_rol: string;
    rol: string;
    id_organizacion?: string;
}

interface OrganizationContextProps {
    // Organizaciones
    selectedOrgId: string | null;
    selectedOrgName: string | null;
    userOrganizations: UserOrganization[];
    isLoadingOrgs: boolean;
    switchOrganization: (orgId: string) => Promise<boolean>;

    // Roles
    selectedRoleId: string | null;
    selectedRoleName: string | null;
    availableRoles: UserRole[];
    switchRole: (roleId: string) => void;

    // Créditos
    creditosUsados: number;
    creditosDisponibles: number;
    isLoadingCreditos: boolean;
    refreshCredits: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextProps>({
    selectedOrgId: null,
    selectedOrgName: null,
    userOrganizations: [],
    isLoadingOrgs: false,
    switchOrganization: async () => false,

    selectedRoleId: null,
    selectedRoleName: null,
    availableRoles: [],
    switchRole: () => { },

    creditosUsados: 0,
    creditosDisponibles: 0,
    isLoadingCreditos: false,
    refreshCredits: async () => { },
});

interface OrganizationProviderProps {
    children: ReactNode;
}

export const OrganizationProvider: FC<OrganizationProviderProps> = ({ children }) => {
    const { user, setUser, setActiveRoleId } = useAuthContext();

    // Org state
    const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
    const [selectedOrgName, setSelectedOrgName] = useState<string | null>(null);
    const [userOrganizations, setUserOrganizations] = useState<UserOrganization[]>([]);
    const [isLoadingOrgs, setIsLoadingOrgs] = useState(false);

    // Role state
    const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);
    const [selectedRoleName, setSelectedRoleName] = useState<string | null>(null);
    const [availableRoles, setAvailableRoles] = useState<UserRole[]>([]);

    // Credits state
    const [creditosUsados, setCreditosUsados] = useState(0);
    const [creditosDisponibles, setCreditosDisponibles] = useState(0);
    const [isLoadingCreditos, setIsLoadingCreditos] = useState(false);

    // ─── localStorage helpers ───
    const getOrgStorageKey = () => user?.id ? `ummia_selected_org_${user.id}` : null;
    const getRoleStorageKey = () => user?.id ? `ummia_selected_role_${user.id}` : null;

    const loadSelectedOrg = () => {
        const key = getOrgStorageKey();
        if (!key) return null;
        try { return localStorage.getItem(key); } catch { return null; }
    };
    const saveSelectedOrg = (orgId: string) => {
        const key = getOrgStorageKey();
        if (key) localStorage.setItem(key, orgId);
    };
    const loadSelectedRole = () => {
        const key = getRoleStorageKey();
        if (!key) return null;
        try { return localStorage.getItem(key); } catch { return null; }
    };
    const saveSelectedRole = (roleId: string) => {
        const key = getRoleStorageKey();
        if (key) localStorage.setItem(key, roleId);
    };

    // ─── Fetch nombre de organización desde creditos-organizacion ───
    const fetchOrgName = async (orgId: string, signal?: AbortSignal): Promise<string | null> => {
        if (!baseApiBroker || !VERSION) return null;
        try {
            const base = String(baseApiBroker).replace(/\/$/, "");
            const url = `${base}/creditos-organizacion?id_organizacion=${encodeURIComponent(orgId)}&version=${encodeURIComponent(VERSION)}`;
            const res = await fetch(url, {
                method: "GET",
                headers: { "Content-Type": "application/json" },
                signal,
            });
            if (!res.ok) return null;
            const data = await res.json();
            if (typeof data?.organizacion === "string" && data.organizacion.trim()) return data.organizacion.trim();
            if (typeof data?.body?.organizacion === "string" && data.body.organizacion.trim()) return data.body.organizacion.trim();
            if (Array.isArray(data?.body) && data.body[0]?.organizacion) return data.body[0].organizacion.trim();
            if (Array.isArray(data) && data[0]?.organizacion) return data[0].organizacion.trim();
            return null;
        } catch {
            return null;
        }
    };

    // ─── Build organizations list when user changes ───
    useEffect(() => {
        if (!user?.id || !user?.organizaciones) {
            setUserOrganizations([]);
            setSelectedOrgId(null);
            setSelectedOrgName(null);
            return;
        }

        const controller = new AbortController();
        setIsLoadingOrgs(true);

        const buildOrgs = async () => {
            const orgIds = user.organizaciones ?? [];
            const isAdminDominio = user.roles?.some(r => r.rol === "Admin de Dominio");

            const orgsData = await Promise.all(
                orgIds.map(async (id) => {
                    const [nombre, resCreditos] = await Promise.all([
                        fetchOrgName(id, controller.signal),
                        getInfoCredits(user.id, id).catch(() => null)
                    ]);

                    const suscripciones = resCreditos?.data?.body?.suscripciones ?? [];
                    const hasSub = suscripciones.length > 0;

                    const hasRoles = user.roles?.some(r => r.id_organizacion === id);

                    let disabled = false;

                    if (!isAdminDominio && !hasRoles) {
                        disabled = true;
                    } else if (!hasSub) {
                        disabled = true;
                    }

                    return {
                        id_organizacion: id,
                        nombre_organizacion: nombre ?? id,
                        disabled
                    };
                })
            );

            if (controller.signal.aborted) return;
            setUserOrganizations(orgsData);

            const savedOrgId = loadSelectedOrg();
            const savedOrg = orgsData.find(o => o.id_organizacion === savedOrgId);

            if (savedOrgId && savedOrg && !savedOrg.disabled) {
                setSelectedOrgId(savedOrgId);
                setSelectedOrgName(savedOrg.nombre_organizacion);
            } else if (orgsData.length > 0) {
                const firstEnabled = orgsData.find(o => !o.disabled) || orgsData[0];
                setSelectedOrgId(firstEnabled.id_organizacion);
                setSelectedOrgName(firstEnabled.nombre_organizacion);
                saveSelectedOrg(firstEnabled.id_organizacion);
            }

            setIsLoadingOrgs(false);
        };

        buildOrgs();
        return () => { controller.abort(); };
    }, [user?.id, user?.organizaciones]);

    // ─── Update available roles when selectedOrgId changes ───
    useEffect(() => {
        if (!user?.roles || !selectedOrgId) {
            setAvailableRoles([]);
            setSelectedRoleId(null);
            setSelectedRoleName(null);
            return;
        }

        const orgRoles = user.roles.filter(r => r.id_organizacion === selectedOrgId);
        const adminDeDominio = user.roles.filter(r =>
            r.rol === "Admin de Dominio" && r.id_organizacion !== selectedOrgId
        );

        const existingIds = new Set(orgRoles.map(r => r.id_rol));
        const filteredRoles = [
            ...orgRoles,
            ...adminDeDominio.filter(r => !existingIds.has(r.id_rol))
        ];

        setAvailableRoles(filteredRoles);

        const savedRoleId = loadSelectedRole();

        if (savedRoleId && filteredRoles.some(r => r.id_rol === savedRoleId)) {
            const savedRole = filteredRoles.find(r => r.id_rol === savedRoleId)!;
            setSelectedRoleId(savedRoleId);
            setSelectedRoleName(savedRole.rol);
            setActiveRoleId(savedRoleId);
        } else if (filteredRoles.length > 0) {
            const adminDominio = filteredRoles.find(r => r.rol === "Admin de Dominio");
            const defaultRole = adminDominio ?? filteredRoles[0];
            setSelectedRoleId(defaultRole.id_rol);
            setSelectedRoleName(defaultRole.rol);
            saveSelectedRole(defaultRole.id_rol);
            setActiveRoleId(defaultRole.id_rol);
        } else {
            setSelectedRoleId(null);
            setSelectedRoleName(null);
            setActiveRoleId(null);
        }
    }, [user?.roles, selectedOrgId]);

    // ─── Fetch credits (reutilizable externamente) ───
    const refreshCredits = async () => {
        if (!user?.id || !selectedOrgId) return;
        setIsLoadingCreditos(true);
        try {
            const res = await getInfoCredits(user.id, selectedOrgId);
            if (res.data.statusCode === 200) {
                const data = res.data.body;
                setCreditosUsados(data?.creditos_gastados_mes ?? 0);
                setCreditosDisponibles(data?.creditos_disponibles ?? 0);
            }
        } catch (error) {
            console.error("[OrganizationContext] Error fetching credits:", error);
        } finally {
            setIsLoadingCreditos(false);
        }
    };

    useEffect(() => {
        refreshCredits();
    }, [user?.id, selectedOrgId]);

    const switchOrganization = async (orgId: string): Promise<boolean> => {
        const org = userOrganizations.find(o => o.id_organizacion === orgId);
        if (!org || !user?.id) return false;

        const isAdminDominio = user.roles?.some(r => r.rol === "Admin de Dominio");
        const hasRolesInOrg = user.roles?.some(r => r.id_organizacion === orgId);

        if (!isAdminDominio && !hasRolesInOrg) {
            toast.error(`No tienes roles asignados en la organización "${org.nombre_organizacion}"`);
            return false;
        }

        try {
            const res = await getInfoCredits(user.id, orgId);
            const suscripciones = res?.data?.body?.suscripciones ?? [];
            if (suscripciones.length === 0) {
                toast.error(`No tienes suscripciones activas en la organización "${org.nombre_organizacion}"`);
                return false;
            }
        } catch {
            toast.error("No se pudo verificar las suscripciones. Intenta de nuevo.");
            return false;
        }

        setSelectedOrgId(orgId);
        setSelectedOrgName(org.nombre_organizacion);
        saveSelectedOrg(orgId);

        setUser(prev => prev ? { ...prev, id_organizacion: orgId } : prev);

        setSelectedRoleId(null);
        setSelectedRoleName(null);
        const roleKey = getRoleStorageKey();
        if (roleKey) localStorage.removeItem(roleKey);

        return true;
    };

    // ─── Switch role ───
    const switchRole = (roleId: string) => {
        const role = availableRoles.find(r => r.id_rol === roleId);
        if (!role) return;

        setSelectedRoleId(roleId);
        setSelectedRoleName(role.rol);
        saveSelectedRole(roleId);
        setActiveRoleId(roleId);
    };

    return (
        <OrganizationContext.Provider
            value={{
                selectedOrgId,
                selectedOrgName,
                userOrganizations,
                isLoadingOrgs,
                switchOrganization,

                selectedRoleId,
                selectedRoleName,
                availableRoles,
                switchRole,

                creditosUsados,
                creditosDisponibles,
                isLoadingCreditos,
                refreshCredits,
            }}
        >
            {children}
        </OrganizationContext.Provider>
    );
};

export const useOrganizationContext = () => useContext(OrganizationContext);
